/**
 * Load Message Handler
 * Handles 'load' message type for full state reloads
 */

import { loadExpandedNodesState } from '@/shared/utils/viewStateStorage';
import type { ProjectData, ProjectNode } from '@/shared/types/projectData';
import { transformProjectDataFromBackend } from '@/shared/utils/fieldNameTransform';

interface LoadMessageHandlerParams {
  message: Record<string, unknown>;
  setProjectData: (data: ProjectData) => void;
  setExpandedNodeIds: (ids: Set<string>) => void;
}

/**
 * Handle 'load' message type (full state reload from import or sync)
 */
export function handleLoadMessage({
  message,
  setProjectData,
  setExpandedNodeIds
}: LoadMessageHandlerParams): void {
  if (!message.data) return;

  const transformedData = transformProjectDataFromBackend(message.data);
  setProjectData(transformedData);

  // Auto-expand groups if it's an import (but respect saved state if available)
  if (message.action === 'import-complete') {
    // Check if user has saved expanded nodes state
    const savedExpandedNodes = loadExpandedNodesState();

    if (savedExpandedNodes && savedExpandedNodes.size > 0) {
      // Use saved state
      setExpandedNodeIds(savedExpandedNodes);
    } else {
      // No saved state - expand all groups by default
      const groupIds = transformedData.nodes
        .filter((node: ProjectNode) => node.type === 'group')
        .map((node: ProjectNode) => node.id);
      setExpandedNodeIds(new Set(groupIds));
    }
  }
}
